import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  TableSortLabel,
} from "@mui/material";
import { getAdminUsers, updateAdminUserMember, deleteAdminUsers } from "../client/admin";

function UserManagePage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editType, setEditType] = useState(0);
  const [editExpires, setEditExpires] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, sortField, sortOrder]);

  function handleSort(field) {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(0);
  }

  function handleSelectOne(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function handleSelectAll() {
    const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map((r) => r.id));
    }
  }

  async function loadData() {
    const res = await getAdminUsers({
      page: page + 1,
      page_size: pageSize,
      search,
      sort_field: sortField,
      sort_order: sortOrder,
    });
    if (res) {
      setRows(res.list || []);
      setTotal(res.total || 0);
    }
  }

  function openEdit(user) {
    setEditUser(user);
    setEditType(user.member_type ?? 0);
    setEditExpires(user.member_expires_at ? user.member_expires_at.slice(0, 16) : "");
    setEditOpen(true);
  }

  async function handleSave() {
    if (!editUser) return;
    const res = await updateAdminUserMember(editUser.id, {
      member_type: editType,
      member_expires_at: editExpires || null,
    });
    if (res?.message === "更新成功") {
      setEditOpen(false);
      loadData();
    } else {
      alert(res?.message || "更新失败");
    }
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        用户管理
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          label="搜索用户名"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadData()}
        />
        <Button variant="contained" onClick={loadData}>
          搜索
        </Button>
        {selectedIds.length > 0 && (
          <Button variant="contained" color="error" onClick={() => setBatchDeleteOpen(true)}>
            批量删除 ({selectedIds.length})
          </Button>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.length > 0 && selectedIds.length < rows.length}
                  checked={rows.length > 0 && rows.every((r) => selectedIds.includes(r.id))}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "id"} direction={sortOrder} onClick={() => handleSort("id")}>
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "username"} direction={sortOrder} onClick={() => handleSort("username")}>
                  用户名
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "member_type"} direction={sortOrder} onClick={() => handleSort("member_type")}>
                  会员类型
                </TableSortLabel>
              </TableCell>
              <TableCell>到期时间</TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "role"} direction={sortOrder} onClick={() => handleSort("role")}>
                  角色
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "created_at"} direction={sortOrder} onClick={() => handleSort("created_at")}>
                  注册时间
                </TableSortLabel>
              </TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(row.id)}
                    onChange={() => handleSelectOne(row.id)}
                  />
                </TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.username}</TableCell>
                <TableCell>{row.member_type === 0 ? "免费" : "会员"}</TableCell>
                <TableCell>
                  {row.member_expires_at
                    ? new Date(row.member_expires_at).toLocaleString()
                    : row.member_type > 0
                    ? "永久"
                    : "-"}
                </TableCell>
                <TableCell>{row.role === 1 ? "管理员" : "用户"}</TableCell>
                <TableCell>
                  {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openEdit(row)}>
                    编辑会员
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={batchDeleteOpen} onClose={() => setBatchDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>确认批量删除</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            确定要删除选中的 {selectedIds.length} 个用户吗？此操作不可恢复，关联的排盘记录也会被一并删除。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              const res = await deleteAdminUsers(selectedIds);
              if (res?.message === "删除成功") {
                setBatchDeleteOpen(false);
                setSelectedIds([]);
                loadData();
              } else {
                alert(res?.message || "删除失败");
              }
            }}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>调整会员</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>会员类型</InputLabel>
            <Select value={editType} label="会员类型" onChange={(e) => setEditType(e.target.value)}>
              <MenuItem value={0}>免费</MenuItem>
              <MenuItem value={1}>会员</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="到期时间"
            type="datetime-local"
            fullWidth
            margin="normal"
            size="small"
            value={editExpires}
            onChange={(e) => setEditExpires(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="留空表示永久"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleSave}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagePage;
